import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ReadOnlyTextProperty } from "@/components/doc/right-sidebar/node-viewer/NodeProperty";
import { NodePropertyInput } from "@/components/doc/right-sidebar/node-viewer/NodePropertyInput";
import { type ViewerNode } from "@/components/doc/right-sidebar/node-viewer/types";
import { setNodeValue } from "@/components/doc/right-sidebar/node-viewer/utils";
import { isNodeEditable } from "@/components/tiptap/lock/utils";

type Props = {
  node: ViewerNode;
  ids: Set<string>;
};

const NodeValuePropertyField = observer(function NodeValuePropertyField({ node, ids }: Props) {
  const t = useTranslations("sidebar");
  const [value, setValue] = useState(() => node.value);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\s/g, "");
    setValue(newValue);
  };

  const handleValueSubmit = () => {
    if (value?.trim()) {
      const nodesToChange = [...ids.values()].map((id) => node.doc.nodes.get(id)!) as ViewerNode[];
      setNodeValue(nodesToChange, value);
    }
  };

  useEffect(() => {
    setValue(node.value);
  }, [node.value]);

  return (
    <NodePropertyInput
      id="value"
      value={value}
      label={t("nodeProps.nodeValue")}
      className="font-mono"
      onChange={handleValueChange}
      onSubmit={handleValueSubmit}
    />
  );
});

const NodeValuePropertyReadOnly = observer(function NodeValuePropertyReadOnly({ node }: Props) {
  const t = useTranslations("sidebar");

  return <ReadOnlyTextProperty name={t("nodeProps.nodeValue")} value={node.value ?? ""} mono />;
});

export const NodeValueProperty = observer(function NodeValueProperty(props: Props) {
  if (!isNodeEditable(props.node)) {
    return <NodeValuePropertyReadOnly {...props} />;
  }

  return <NodeValuePropertyField {...props} />;
});
